import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  MenuItem,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import userApi from "../api/userApi";
import userGroupApi from "../api/userGroupApi";
import ConfirmDialog from "../components/ConfirmDialog";
import InformationDialog from "../components/InformationDialog";

function isValidEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}
const pageNo = "UsersPage";

function UsersPage() {
  const roles = useSelector((state) => state.auth.roles) || null;
  const user = useSelector((state) => state.auth.user);
  const customerInfor = useSelector((state) => state.auth.customerInfor);

  // --- Trạng thái tìm kiếm và lọc ---
  const [searchKeyword, setSearchKeyword] = useState("");
  const searchInputRef = useRef(null);
  const [reloadDataTrigger, setReloadDataTrigger] = useState(0);

  // --- Trạng thái DataGrid và phân trang ---
  const [users, setUsers] = useState([]); // Danh sách người dùng hiển thị trên DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false); // Trạng thái loading chung cho DataGrid và các thao tác

  // --- Trạng thái Modal thêm, sửa người dùng ---
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  // Modal thêm/sửa
  const [formErrors, setFormErrors] = useState({}); // Lỗi validation form
  const [editUser, setEditUser] = useState({
    UsId: null,
    UsFullName: "",
    UsPhoneNumber: "",
    UsEmail: "",
    CusId: customerInfor.CusId,
    UgId: null,
    IsNew: true,
  });

  // --- Trạng thái Modal đổi mật khẩu ---
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [userIdToChangePassword, setUserIdToChangePassword] = useState(null);
  const oldPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const [passwordErrors, setPasswordErrors] = useState({});

  /*Vai trò*/
  const [userGroups, setUserGroups] = useState([]);

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    onClose: () => {},
  });
  const [informationDialogState, setInformationDialogState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    confirmText: "",
  });

  const fetchUserGroup = useCallback(async () => {
    try {
      const response = await userGroupApi.searchUserGroups(
        customerInfor.CusId,
        "",
        0,
        100,
        true,
      );
      if (response.success && response.data) {
        setUserGroups(
          response.data.map((ug) => ({
            value: ug.UgId,
            label: ug.UgName,
          })),
        );
      } else {
        toast.error(response.message || "Không thể tải danh sách vai trò.");
      }
    } catch (error) {
      toast.error(error.toString() || "Lỗi kết nối khi tải danh sách vai trò");
    } finally {
    }
  }, [customerInfor]);
  useEffect(() => {
    fetchUserGroup();
  }, [fetchUserGroup]);

  // --- Hàm tìm kiếm người dùng ---
  const handleSearch = useCallback(
    async (
      page = paginationModel.page,
      pageSize = paginationModel.pageSize,
    ) => {
      setLoading(true);
      try {
        const response = await userApi.searchUsers(
          customerInfor.CusId,
          searchKeyword,
          page,
          pageSize,
          customerInfor.CusIsAdmin,
        );

        if (response.success) {
          setUsers(response.data);
          setTotalRowCount(response.pagination?.totalRecords || 0);
        } else {
          toast.warning(
            response.message || "Không tìm thấy dữ liệu người dùng.",
          );
          setUsers([]);
          setTotalRowCount(0);
        }
      } catch (error) {
        toast.error(
          error.toString() || "Đã xảy ra lỗi khi tìm kiếm người dùng.",
        );
        setUsers([]);
        setTotalRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [searchKeyword, paginationModel, customerInfor],
  );

  // --- Phân trang DataGrid ---
  const handlePaginationModelChange = useCallback((newModel) => {
    setPaginationModel(newModel);
  }, []);

  // --- Áp dụng tìm kiếm (khi nhấn nút hoặc Enter) ---
  const handleApplySearch = useCallback(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSearchKeyword(
      searchInputRef.current ? searchInputRef.current.value : "",
    );
    setReloadDataTrigger((prev) => prev + 1);
  }, []);

  // --- Thêm người dùng mới ---
  const handleAddUser = () => {
    if (
      !roles.some(
        (x) =>
          x.PvNo === pageNo && x.CtrNo === "btnAdd" && x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền thêm người dùng.");
      return;
    }

    setEditUser({
      UsId: null,
      UsFullName: "",
      UsPhoneNumber: "",
      UsEmail: "",
      CusId: customerInfor.CusId,
      UgId: null,
      IsNew: true,
    });
    setOpenAddEditModal(true);
  };

  // --- Sửa người dùng ---
  const handleEditUser = (userId) => {
    if (
      !roles.some(
        (x) =>
          x.PvNo === pageNo && x.CtrNo === "btnEdit" && x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền sửa thông tin người dùng.");
      return;
    }
    const userToEdit = users.find((u) => u.UsId === userId);
    if (userToEdit) {
      setFormErrors({});
      setOpenAddEditModal(true);
      setEditUser({
        UsId: userToEdit.UsId,
        UsFullName: userToEdit.UsFullName,
        UsPhoneNumber: userToEdit.UsPhoneNumber,
        UsEmail: userToEdit.UsEmail,
        CusId: userToEdit.CusId,
        UgId: userToEdit.UgId,
        IsNew: false,
      });
    } else {
      toast.error("Không tìm thấy người dùng để chỉnh sửa.");
    }
  };

  // --- Đóng Modal thêm/sửa ---
  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setFormErrors({});
    setEditUser({
      UsId: null,
      UsFullName: "",
      UsPhoneNumber: "",
      UsEmail: "",
      CusId: customerInfor.CusId,
      UgId: null,
      IsNew: true,
    });
  };

  // --- Lưu người dùng (thêm mới hoặc cập nhật) ---
  const handleSaveUser = async () => {
    const errors = {};
    if (!editUser.UsEmail) errors.email = "Email không được để trống";
    else if (!editUser.UsId && !isValidEmail(editUser.UsEmail))
      errors.email = "Email không hợp lệ";
    if (!editUser.UsFullName)
      errors.UsFullName = "Họ và tên không được để trống";
    if (!editUser.UgId) errors.userGroup = "Vui lòng chọn vai trò.";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    try {
      let userData = {
        UsId: editUser.UsId,
        UsFullName: editUser.UsFullName,
        UsPhoneNumber: editUser.UsPhoneNumber,
        UsEmail: editUser.UsEmail,
        CusId: editUser.CusId,
        UgId: editUser.UgId,
        IsNew: editUser.IsNew,
      };
      const response = await userApi.upsertUser(userData);

      if (response.success) {
        toast.success(response.message || "Lưu người dùng thành công!");
        handleCloseAddEditModal();
        setReloadDataTrigger((prev) => prev + 1);
      } else {
        toast.error(response.message || "Lưu người dùng thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi lưu người dùng.");
    } finally {
      setLoading(false);
    }
  };

  // --- Xóa người dùng ---
  const handleDeleteUser = async (rowUser) => {
    if (user.UsId === rowUser.UsId) {
      toast.warning("Bạn không thể xoá người dùng hiện tại.");
      return;
    }
    if (rowUser.UsIsAdmin) {
      if (!customerInfor.CusIsAdmin || !user.UsIsAdmin) {
        toast.warning("Bạn không thể xoá người dùng admin.");
        return;
      }
    }
    if (
      !roles.some(
        (x) =>
          x.PvNo === pageNo && x.CtrNo === "btnDelete" && x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền xoá người dùng.");
      return;
    }
    setLoading(true);
    try {
      const response = await userApi.deleteUser(
        rowUser.UsId,
        customerInfor.CusId,
      );
      if (response.success) {
        toast.success(response.message || "Xóa người dùng thành công!");
        setReloadDataTrigger((prev) => prev + 1);
      } else {
        toast.error(response.message || "Xóa người dùng thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi xóa người dùng.");
    } finally {
      setLoading(false);
      setConfirmDialogState({
        ...confirmDialogState,
        open: false,
      });
    }
  };

  // --- Mở Modal đổi mật khẩu ---
  const handleOpenChangePasswordModal = (userId) => {
    if (
      !roles.some(
        (x) =>
          x.PvNo === pageNo &&
          x.CtrNo === "btnChangePasswordUser" &&
          x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền đổi mật khẩu người dùng.");
      return;
    }
    setUserIdToChangePassword(userId);
    setPasswordErrors({});
    setOpenChangePasswordModal(true);
  };

  // --- Đóng Modal đổi mật khẩu ---
  const handleCloseChangePasswordModal = () => {
    setOpenChangePasswordModal(false);
    setUserIdToChangePassword(null);
    setPasswordErrors({});
    if (oldPasswordRef.current) oldPasswordRef.current.value = "";
    if (newPasswordRef.current) newPasswordRef.current.value = "";
    if (confirmPasswordRef.current) confirmPasswordRef.current.value = "";
  };

  // --- Lưu mật khẩu mới ---
  const handleSaveNewPassword = async () => {
    // Lấy giá trị từ ref
    const oldPassword = oldPasswordRef.current.value;
    const newPassword = newPasswordRef.current.value;
    const confirmPassword = confirmPasswordRef.current.value;

    const errors = {};
    if (!oldPassword) errors.oldPassword = "Mật khẩu cũ không được để trống.";
    if (!newPassword) errors.newPassword = "Mật khẩu mới không được để trống.";
    if (newPassword !== confirmPassword)
      errors.confirmPassword = "Mật khẩu xác nhận không khớp.";

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const response = await userApi.changePassword(
        userIdToChangePassword,
        oldPassword,
        newPassword,
      );

      if (response.success) {
        toast.success(response.message || "Đổi mật khẩu thành công!");
        handleCloseChangePasswordModal();
      } else {
        toast.error(response.message || "Đổi mật khẩu thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi đổi mật khẩu.");
    } finally {
      setLoading(false);
    }
  };

  // --- Định nghĩa các cột cho DataGrid ---
  const columns = [
    {
      field: "UsFullName",
      headerName: "Họ và tên",
      headerAlign: "center",
      width: 350,
    },
    {
      field: "UsEmail",
      headerName: "Email",
      headerAlign: "center",
      width: 300,
    },
    {
      field: "UgName",
      headerName: "Vai trò",
      headerAlign: "center",
      align: "center",
      width: 200,
    },
    {
      field: "UsPhoneNumber",
      headerName: "Số điện thoại",
      headerAlign: "center",
      align: "center",
      width: 150,
    },
    {
      field: "actions",
      headerName: "Chức năng",
      headerAlign: "center",
      type: "actions",
      width: 170,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Sửa">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEditUser(params.row.UsId)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Đổi mật khẩu">
            <IconButton
              color="secondary"
              size="small"
              onClick={() =>
                setConfirmDialogState({
                  open: true,
                  title: "Đổi mật khẩu",
                  content: "Bạn chắc chắn muốn đổi mật khẩu?",
                  onConfirm: () => {
                    setConfirmDialogState({
                      ...confirmDialogState,
                      open: false,
                    });
                    handleChangePass(params.row.UsId);
                  },
                  onClose: () =>
                    setConfirmDialogState({
                      ...confirmDialogState,
                      open: false,
                    }),
                })
              }
            >
              <KeyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Xoá">
            <IconButton
              color="error"
              size="small"
              onClick={() =>
                setConfirmDialogState({
                  open: true,
                  title: "Xoá người dùng",
                  content: "Bạn chắc chắn muốn xoá người dùng này?",
                  onConfirm: () => handleDeleteUser(params.row),
                  onClose: () =>
                    setConfirmDialogState({
                      ...confirmDialogState,
                      open: false,
                    }),
                })
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];
  const handleChangePass = async (UsId) => {
    try {
      const response = await userApi.SendEmailChangePassword(
        UsId,
        customerInfor.CusId,
      );
      if (response.success) {
        setInformationDialogState({
          open: true,
          title: "Đổi mật khẩu",
          content:
            "Chúng tôi đã gửi liên kết để đặt lại mật khẩu đến email của bạn. Vui lòng kiểm tra email.",
          onConfirm: () => {
            setInformationDialogState({
              ...informationDialogState,
              open: false,
            });
            setConfirmDialogState({
              ...confirmDialogState,
              open: false,
            });
          },
        });
      } else {
        toast.error(response.message || "Đổi mật khẩu thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đổi mật khẩu thất bại.");
    }
  };
  const checkEmail = async () => {
    try {
      const response = await userApi.CheckEmail(
        customerInfor.CusId,
        editUser.UsEmail,
      );
      const errors = {};
      if (response.success) {
        if (response.data) {
          setEditUser({
            ...editUser,
            UsId: response.data.UsId,
            UsFullName: response.data.UsFullName,
            UsPhoneNumber: response.data.UsPhoneNumber,
          });
        }
      } else {
        errors.email = response.message;
      }
      setFormErrors(errors);
    } catch (error) {
      toast.error(error.toString());
    }
  };

  useEffect(() => {
    handleSearch();
  }, [handleSearch, reloadDataTrigger]);

  return (
    <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={3}>
            <TextField
              fullWidth
              label="Từ khoá"
              variant="outlined"
              size="small"
              inputRef={searchInputRef}
              defaultValue={searchKeyword}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleApplySearch();
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleApplySearch}
              sx={{ height: "40px" }}
            >
              Tìm kiếm
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
        >
          Mời người dùng
        </Button>
      </Box>

      <Box sx={{ flexGrow: 1, width: "100%" }}>
        <DataGrid
          rows={users}
          columns={columns}
          paginationMode="server"
          rowCount={totalRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          loading={loading}
          getRowId={(row) => row.UsId}
        />
      </Box>

      <Dialog
        open={openAddEditModal}
        onClose={handleCloseAddEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editUser?.IsNew ? "Mời người dùng mới" : "Sửa thông tin người dùng"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={editUser.UsEmail}
            onChange={(e) =>
              setEditUser({ ...editUser, UsEmail: e.target.value })
            }
            onBlur={checkEmail}
            error={!!formErrors.email}
            helperText={formErrors.email}
            size="small"
            disabled={editUser?.IsNew === false}
            required
          />
          <TextField
            margin="dense"
            id="UsFullName"
            label="Họ và tên"
            type="text"
            fullWidth
            variant="outlined"
            value={editUser.UsFullName}
            onChange={(e) =>
              setEditUser({ ...editUser, UsFullName: e.target.value })
            }
            error={!!formErrors.UsFullName}
            helperText={formErrors.UsFullName}
            size="small"
            required
          />
          <TextField
            margin="dense"
            id="phoneNumber"
            label="Số điện thoại"
            type="text"
            fullWidth
            variant="outlined"
            value={editUser.UsPhoneNumber}
            onChange={(e) =>
              setEditUser({ ...editUser, UsPhoneNumber: e.target.value })
            }
            error={!!formErrors.phoneNumber}
            helperText={formErrors.phoneNumber}
            size="small"
          />
          <TextField
            margin="dense"
            select
            label="Vai trò"
            fullWidth
            value={editUser?.UgId}
            onChange={(e) => setEditUser({ ...editUser, UgId: e.target.value })}
            size="small"
            error={!!formErrors.userGroup}
            helperText={formErrors.userGroup}
            required
          >
            {userGroups.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddEditModal} color="primary">
            Hủy
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Đổi mật khẩu */}
      <Dialog
        open={openChangePasswordModal}
        onClose={handleCloseChangePasswordModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Đổi mật khẩu</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="oldPassword"
            label="Mật khẩu cũ"
            type="password"
            fullWidth
            variant="outlined"
            inputRef={oldPasswordRef}
            error={!!passwordErrors.newPassword}
            helperText={passwordErrors.newPassword}
            sx={{ mb: 2 }}
            size="medium"
          />
          <TextField
            id="newPassword"
            label="Mật khẩu mới"
            type="password"
            fullWidth
            variant="outlined"
            inputRef={newPasswordRef}
            error={!!passwordErrors.newPassword}
            helperText={passwordErrors.newPassword}
            sx={{ mb: 2 }}
            size="medium"
          />
          <TextField
            id="confirmPassword"
            label="Xác nhận mật khẩu mới"
            type="password"
            fullWidth
            variant="outlined"
            inputRef={confirmPasswordRef}
            error={!!passwordErrors.confirmPassword}
            helperText={passwordErrors.confirmPassword}
            sx={{ mb: 2 }}
            size="medium"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangePasswordModal} color="primary">
            Hủy
          </Button>
          <Button
            onClick={handleSaveNewPassword}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Xác nhận"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialogState.open}
        title={confirmDialogState.title}
        content={confirmDialogState.content}
        onConfirm={confirmDialogState.onConfirm}
        onClose={confirmDialogState.onClose}
      />
      <InformationDialog
        open={informationDialogState.open}
        title={informationDialogState.title}
        content={informationDialogState.content}
        onConfirm={informationDialogState.onConfirm}
      />
    </Box>
  );
}

export default UsersPage;
